import React, { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import * as CommonIcons from '../../CommonIcons';
import MoveRecordsModal from '../RecordsList/MoveRecordsModal';
import ModalConfirm from '../ModalConfirm';
import { useToastContext } from '../../../toasts/toast-context';

import styles from './UserViewOneRecord.module.scss';

export default function RecordActionToolbar({ userUid, recordUid, description, allowEdit, allowDelete, allowMove, onRecordDeleted, onRecordMoved }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showErrorToastMessage } = useToastContext();

  const [includedInTrends, setIncludedInTrends] = useState(description?.includedInTrends);
  const [isShowInEcg12, setIsShowInEcg12] = useState(description?.isShowInEcg12);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const recordListUrl = `/user/${userUid}/records`;

  const onToggleTrends = () => {
    const previousValue = includedInTrends;
    const newValue = !includedInTrends;
    setIncludedInTrends(newValue);

    const request = newValue
      ? api.post(`${recordListUrl}/${recordUid}/include-in-trends`)
      : api.delete(`${recordListUrl}/${recordUid}/include-in-trends`);

    request.catch(() => {
      setIncludedInTrends(previousValue);
      showErrorToastMessage(t('common.error'));
    });
  };

  const onToggleEcg12 = () => {
    const newValue = !isShowInEcg12;
    setIsShowInEcg12(newValue);

    api.post(`${recordListUrl}/${recordUid}/ecg12`, { showInEcg12: newValue })
      .then((response) => {
        if (!response.data)
          setIsShowInEcg12(false);
      })
      .catch(() => {
        setIsShowInEcg12(!newValue);
        showErrorToastMessage(t('common.error'));
      });
  };

  const onEditClick = () => {
    navigate(`/user/${userUid}/records/${recordUid}/edit`);
  };

  const onDeleteConfirm = () => {
    api.delete(recordListUrl, { data: { selectedUids: [recordUid] } })
      .then(() => {
        if (onRecordDeleted) onRecordDeleted();
      })
      .catch(() => {
        showErrorToastMessage(t('common.error'));
      });
  };

  const onMoveSuccess = () => {
    if (onRecordMoved) onRecordMoved();
  };

  const hasAnyAction = allowEdit || allowDelete || allowMove;
  if (!hasAnyAction) return null;

  const canBeIncludedInTrends = description?.canBeIncludedInTrends;
  const canBeUsedForEcg12 = description?.canBeUsedForEcg12;
  const hasMenuItems = allowMove || allowDelete;

  return (
    <>
      <div className={styles.recordToolbar}>
        {allowEdit && (
          <>
            {canBeIncludedInTrends ? (
              <Button
                className={`${styles.toolbarButton} ${styles.btnHoverable}`}
                variant="outline-dark"
                size="sm"
                onClick={onToggleTrends}
                title={includedInTrends ? t('recordView.actions.removeFromTrends') : t('recordView.actions.addToTrends')}
              >
                {!includedInTrends && (
                  <>
                    <span className={styles.btnNoHover}><CommonIcons.AddToTrends className={styles.toolbarIcon} /></span>
                    <span className={styles.btnHover}><CommonIcons.AddToTrendsHover className={styles.toolbarIcon} /></span>
                  </>
                )}
                {includedInTrends && (
                  <>
                    <span className={styles.btnNoHover}><CommonIcons.RemoveFromTrends className={styles.toolbarIcon} /></span>
                    <span className={styles.btnHover}><CommonIcons.RemoveFromTrendsHover className={styles.toolbarIcon} /></span>
                  </>
                )}
                {includedInTrends ? t('recordView.actions.removeFromTrends') : t('recordView.actions.addToTrends')}
              </Button>
            ) : (
              <Button className={styles.toolbarButton} variant="outline-secondary" size="sm" disabled title={t('recordList.actions.cannotAddToTrends')}>
                <CommonIcons.AddToTrendsDisabled className={styles.toolbarIcon} />
                {t('recordView.actions.addToTrends')}
              </Button>
            )}

            {canBeUsedForEcg12 ? (
              <Button
                className={`${styles.toolbarButton} ${styles.btnHoverable}`}
                variant="outline-dark"
                size="sm"
                onClick={onToggleEcg12}
                title={isShowInEcg12 ? t('recordView.actions.removeFromEcg12') : t('recordView.actions.addToEcg12')}
              >
                {!isShowInEcg12 && (
                  <>
                    <span className={styles.btnNoHover}><CommonIcons.AddToEcg12 className={styles.toolbarIcon} /></span>
                    <span className={styles.btnHover}><CommonIcons.AddToEcg12Hover className={styles.toolbarIcon} /></span>
                  </>
                )}
                {isShowInEcg12 && (
                  <>
                    <span className={styles.btnNoHover}><CommonIcons.RemoveFromEcg12 className={styles.toolbarIcon} /></span>
                    <span className={styles.btnHover}><CommonIcons.RemoveFromEcg12Hover className={styles.toolbarIcon} /></span>
                  </>
                )}
                {isShowInEcg12 ? t('recordView.actions.removeFromEcg12') : t('recordView.actions.addToEcg12')}
              </Button>
            ) : (
              <Button className={styles.toolbarButton} variant="outline-secondary" size="sm" disabled title={t('recordList.actions.cannotAddToEcg12')}>
                <CommonIcons.AddToEcg12Disabled className={styles.toolbarIcon} />
                {t('recordView.actions.addToEcg12')}
              </Button>
            )}

            <Button className={styles.toolbarButton} variant="outline-dark" size="sm" onClick={onEditClick}>
              <CommonIcons.PensilEdit className={styles.toolbarIcon} />
              {t('recordView.actions.edit')}
            </Button>
          </>
        )}

        {hasMenuItems && (
          <>
            <div className={styles.toolbarSeparator} />
            <Dropdown align="end" className={styles.toolbarMoreMenu}>
              <Dropdown.Toggle variant="outline-dark" size="sm" className={styles.toolbarButton}>
                <i className="bi bi-three-dots-vertical"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {allowMove && (
                  <Dropdown.Item className={styles.dropdownItem} onClick={() => setShowMoveModal(true)}>
                    <CommonIcons.MoveRecord className={styles.dropdownIcon} />
                    {t('recordView.actions.move')}
                  </Dropdown.Item>
                )}
                {allowDelete && (
                  <Dropdown.Item className={`${styles.dropdownItem} text-danger`} onClick={() => setShowDeleteConfirm(true)}>
                    <CommonIcons.TrashCan className={styles.dropdownIcon} />
                    {t('recordView.actions.delete')}
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </>
        )}
      </div>

      {showMoveModal && (
        <MoveRecordsModal
          show={showMoveModal}
          onHide={() => setShowMoveModal(false)}
          records={[recordUid]}
          onSuccessCallback={onMoveSuccess}
        />
      )}

      {showDeleteConfirm && (
        <ModalConfirm
          show={showDeleteConfirm}
          onHide={() => setShowDeleteConfirm(false)}
          header={t('recordList.deleteRecord.title')}
          body={t('recordView.actions.confirmDelete')}
          buttonConfirmText={t('recordList.deleteRecord.confirmButton')}
          buttonCancelText={t('recordList.deleteRecord.cancelButton')}
          onConfirmClick={onDeleteConfirm}
        />
      )}
    </>
  );
}
